import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Folder } from 'lucide-react';

import { useTranslation } from '@sshop/i18n';
import { cn } from '@sshop/ui';

import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';

import { categoriesMenuStyles as s } from './CategoriesMenu.styles';
import { CategoriesMenuPromoCard } from './CategoriesMenuPromoCard';
import type { CategoriesMegaPanelProps } from './CategoriesMenu.types';

const HOVER_OPEN_DELAY = 70;

/**
 * Two-level mega menu:
 *  - Column 1: root category rail.
 *  - Column 2: subcategory tile grid for the active root (terminates here; click goes to listing).
 *  - Column 3 (xl+): up to two featured promos, stacked vertically.
 */
export function CategoriesMegaPanel({ roots, promos, onNavigate }: CategoriesMegaPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [activeRootId, setActiveRootId] = useState<string | null>(roots[0]?.id ?? null);
  const hoverTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!activeRootId && roots[0]) setActiveRootId(roots[0].id);
  }, [activeRootId, roots]);

  const activeRoot = useMemo(
    () => roots.find((r) => r.id === activeRootId) ?? null,
    [activeRootId, roots],
  );
  const subs = activeRoot?.children ?? [];

  const setRootHover = (id: string) => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setActiveRootId(id), HOVER_OPEN_DELAY);
  };

  return (
    <div
      className={s.panel}
      role="dialog"
      aria-label={t('nav.categories')}
      data-categories-menu-panel
    >
      <div className={cn(s.panelInner, promos.length > 0 && s.panelInnerWithPromo)}>
        {/* Rail: root categories */}
        <aside className={s.rail}>
          <div className={s.colHeader}>{t('nav.categories')}</div>
          <ul className={s.railList}>
            {roots.map((root) => (
              <li key={root.id}>
                <Link
                  to={routes.catalog(locale, root.slug)}
                  className={s.rootRow}
                  data-active={root.id === activeRootId || undefined}
                  onMouseEnter={() => setRootHover(root.id)}
                  onFocus={() => setActiveRootId(root.id)}
                  onClick={onNavigate}
                >
                  <span className={s.rootThumb} aria-hidden>
                    {(root.iconUrl ?? root.imageUrl) ? (
                      <img
                        src={root.iconUrl ?? root.imageUrl ?? undefined}
                        alt=""
                        className={s.rootThumbImg}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <Folder className="h-4 w-4" />
                    )}
                  </span>
                  <span className={s.rootLabel}>{root.name}</span>
                  {root.productCount > 0 ? (
                    <span className={s.rootBadge}>{root.productCount}</span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
          <Link to={routes.categories(locale)} className={s.railViewAll} onClick={onNavigate}>
            <span>{t('home.categories.viewAll')}</span>
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </aside>

        {/* Tiles: subcategories of active root */}
        <section className={s.tilesWrap} aria-live="polite">
          <header className={s.tilesHeader}>
            <h3 className={s.tilesTitle}>{activeRoot?.name ?? '\u00A0'}</h3>
            {activeRoot ? (
              <Link
                to={routes.catalog(locale, activeRoot.slug)}
                className={s.tilesViewAll}
                onClick={onNavigate}
              >
                <span>{t('nav.categoriesSeeAllSubs')}</span>
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            ) : null}
          </header>

          {subs.length === 0 ? (
            <p className={s.emptyCol}>{t('nav.categoriesNoSubs')}</p>
          ) : (
            <ul className={s.tilesGrid}>
              {subs.map((sub) => (
                <li key={sub.id}>
                  <Link
                    to={routes.catalog(locale, sub.slug)}
                    className={s.tile}
                    onClick={onNavigate}
                  >
                    <span className={s.tileThumb} aria-hidden>
                      {(sub.iconUrl ?? sub.imageUrl) ? (
                        <img
                          src={sub.iconUrl ?? sub.imageUrl ?? undefined}
                          alt=""
                          className={s.rootThumbImg}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <Folder className="h-5 w-5" />
                      )}
                    </span>
                    <span className={s.tileBody}>
                      <span className={s.tileLabel}>{sub.name}</span>
                      {sub.productCount > 0 ? (
                        <span className={s.tileCount}>
                          {t('nav.categoriesCount', { count: sub.productCount })}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {promos.length > 0 ? (
          <aside className={s.featuredList} aria-label={t('nav.categoriesFeatured')}>
            {promos.map((banner) => (
              <CategoriesMenuPromoCard key={banner.id} banner={banner} onNavigate={onNavigate} />
            ))}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
